import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { CooperationApplyInputSchema, RASPIL_CATEGORY } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import {
  CooperationFormView,
  type CooperationFormSubmitState,
  type CooperationFormValues,
} from './CooperationFormView';

/**
 * Container Экрана 2.9.2 «Анкета поставщика» — Блок 9 (ADR-0005, ADR-0086).
 *
 * Держит state полей + submit (ADR-0005 Container/Presenter, как FeedbackForm).
 * `service` читается из query (?service=) — без неё анкета бессмысленна →
 * `Navigate('/cooperation', replace)`.
 *
 * **Submit (P-021 5 состояний idle/loading/error):**
 *   - Собирает payload ТОЛЬКО из показанных полей: customService кладётся ⟺
 *     service==='other', serviceSubcategory ⟺ service===RASPIL_CATEGORY (иначе
 *     superRefine контракта «запрещено» отклонит). Пустые опциональные поля
 *     (contactPerson/мессенджеры) не кладём — '' falsy, ≥1-мессенджер refine
 *     срабатывает корректно.
 *   - `CooperationApplyInputSchema.safeParse` ДО сети (P-009 защита уровня 2);
 *     !success → generic error «Проверьте поля формы».
 *   - `apiClient.cooperation.apply` (публичный эндпоинт, auth:false) →
 *     `/cooperation/sent`. Ошибка сети/4xx/5xx → error-состояние.
 *
 * Эндпоинт публичный → НЕТ paywall/auth-флоу. PDF (pdfR2Key)/Turnstile/rate-limit —
 * Фаза 2, в этой форме не участвуют.
 */

const EMPTY_VALUES: CooperationFormValues = {
  customService: '',
  serviceSubcategory: '',
  city: '',
  companyOrName: '',
  contactPerson: '',
  address: '',
  whatsapp: '',
  telegram: '',
  max: '',
  message: '',
};

export function CooperationForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const service = params.get('service') ?? '';

  const [values, setValues] = useState<CooperationFormValues>(EMPTY_VALUES);
  const [submitState, setSubmitState] = useState<CooperationFormSubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleFieldChange = useCallback((field: keyof CooperationFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitState('loading');
    setErrorMessage(null);

    // Собираем payload — только показанные/непустые поля (скрытые НЕ кладём,
    // иначе superRefine «запрещено» сработает). trim() убирает случайные пробелы.
    const payload: Record<string, unknown> = {
      service,
      city: values.city,
      companyOrName: values.companyOrName.trim(),
      address: values.address.trim(),
      message: values.message,
    };
    if (service === 'other' && values.customService.trim()) {
      payload.customService = values.customService.trim();
    }
    if (service === RASPIL_CATEGORY && values.serviceSubcategory) {
      payload.serviceSubcategory = values.serviceSubcategory;
    }
    if (values.contactPerson.trim()) payload.contactPerson = values.contactPerson.trim();
    if (values.whatsapp.trim()) payload.whatsapp = values.whatsapp.trim();
    if (values.telegram.trim()) payload.telegram = values.telegram.trim();
    if (values.max.trim()) payload.max = values.max.trim();

    const parsed = CooperationApplyInputSchema.safeParse(payload);
    if (!parsed.success) {
      setErrorMessage('Проверьте поля формы');
      setSubmitState('error');
      return;
    }

    try {
      await apiClient.cooperation.apply(parsed.data);
      navigate('/cooperation/sent');
    } catch {
      // Публичный эндпоинт — UNAUTHORIZED не ожидается; любой сбой
      // (ApiClientError 4xx/5xx, сеть, PARSE_ERROR) → generic сообщение.
      setErrorMessage('Произошла ошибка. Попробуйте ещё раз');
      setSubmitState('error');
    }
  }, [service, values, navigate]);

  // Без service анкета бессмысленна — возвращаем на экран выбора услуги (2.9.1).
  if (!service) {
    return <Navigate to="/cooperation" replace />;
  }

  return (
    <CooperationFormView
      service={service}
      values={values}
      submitState={submitState}
      errorMessage={errorMessage}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
    />
  );
}
